import { useEffect, useMemo, useState } from 'react';
import { Select, Switch, TextField } from '@nyce/admin-kit/ui';
import type { FeatureOverrides, ModelSettings, ModelSettingsOverrides } from '../../api/setup';
import './setup.css';

// The two cascades of nyce.buddy (engine settings and feature flags), reviewed from buddy3's admin
// `components/common.tsx`: every level stores only its overrides; a field shows the inherited value until "Override".

type SettingType = 'text' | 'number' | 'boolean' | 'select';

export interface SettingSpec {
  key: keyof ModelSettings;
  label: string;
  type: SettingType;
  help: string;
  options?: { value: string; label: string }[];
  placeholder?: string;
  /** Only on the global level (no effect below it). */
  globalOnly?: boolean;
  /** Optional on the global level: empty = the fallback named in `help`. */
  optional?: boolean;
  /** Assumed when no level stores a value. */
  default?: string | number | boolean;
}

export interface SettingGroup {
  id: string;
  title: string;
  description: string;
  keys: SettingSpec[];
}

const THINKING = ['MINIMAL', 'LOW', 'MEDIUM', 'HIGH', 'DYNAMIC'].map((v) => ({
  value: v,
  label: v === 'DYNAMIC' ? 'Dynamic (model decides)' : v.charAt(0) + v.slice(1).toLowerCase(),
}));

/** Grouped ModelSettings spec; the order here is the order on screen. */
export const SETTING_GROUPS: SettingGroup[] = [
  {
    id: 'model',
    title: 'Model',
    description:
      'Which Vertex models answer and route, per level of the chat’s thinking picker. Gemini or Claude (claude-* ids, enabled in Model Garden for the project and served in the location); every model needs a row in the price list.',
    keys: [
      {
        key: 'model',
        label: 'Normal model',
        type: 'text',
        help: 'Writes the answers at “Normal”, and everywhere outside the chat picker (monitors, cards, MCP, query from text), e.g. gemini-3.8-flash or claude-haiku-5-5.',
        placeholder: 'gemini-3.8-flash',
      },
      {
        key: 'routeModel',
        label: 'Route model',
        type: 'text',
        help: 'Lite model that picks the schema files and refreshes conversation summaries.',
      },
      {
        key: 'highModel',
        label: 'High model',
        type: 'text',
        help: 'Writes the answers at “High”, e.g. claude-sonnet-5-5; empty = the normal model.',
        optional: true,
      },
      {
        key: 'expertModel',
        label: 'Expert model',
        type: 'text',
        help: 'Writes the answers at “Expert”, e.g. claude-opus-5-5; empty = the normal model.',
        optional: true,
      },
    ],
  },
  {
    id: 'thinking',
    title: 'Thinking',
    description:
      'Reasoning per level of the chat’s thinking picker, and the preselected level. Thoughts are billed as output tokens, so the defaults are Low / Medium / High.',
    keys: [
      {
        key: 'thinking',
        label: 'Normal level',
        type: 'select',
        options: THINKING,
        help: 'Thinking at “Normal” (default Low).',
      },
      {
        key: 'thinkingHigh',
        label: 'High level',
        type: 'select',
        options: THINKING,
        help: 'Thinking at “High” (default Medium).',
        optional: true,
      },
      {
        key: 'thinkingExpert',
        label: 'Expert level',
        type: 'select',
        options: THINKING,
        help: 'Thinking at “Expert” (default High).',
        optional: true,
      },
      {
        key: 'defaultThinking',
        label: 'Default level',
        type: 'select',
        options: [
          { value: 'normal', label: 'Normal' },
          { value: 'high', label: 'High' },
          { value: 'expert', label: 'Expert' },
        ],
        help: 'Level a new conversation starts with.',
      },
    ],
  },
  {
    id: 'agent',
    title: 'Agent loop',
    description: 'Budget and schema exposure of the query loop: steps, sample data, rows.',
    keys: [
      {
        key: 'maxSteps',
        label: 'Max steps (Normal)',
        type: 'number',
        help: 'Hard stop for tool rounds per turn.',
      },
      {
        key: 'targetSteps',
        label: 'Target steps (Normal)',
        type: 'number',
        help: 'Rounds the model aims for before answering.',
      },
      {
        key: 'maxStepsHigh',
        label: 'Max steps (High)',
        type: 'number',
        help: 'Empty = same as Normal.',
        optional: true,
      },
      {
        key: 'targetStepsHigh',
        label: 'Target steps (High)',
        type: 'number',
        help: 'Empty = same as Normal.',
        optional: true,
      },
      {
        key: 'maxStepsExpert',
        label: 'Max steps (Expert)',
        type: 'number',
        help: 'Empty = same as Normal.',
        optional: true,
      },
      {
        key: 'targetStepsExpert',
        label: 'Target steps (Expert)',
        type: 'number',
        help: 'Empty = same as Normal.',
        optional: true,
      },
      {
        key: 'maxRouteFiles',
        label: 'Max routed files',
        type: 'number',
        help: 'Schema files the route model may pick per question.',
      },
      {
        key: 'sampleRows',
        label: 'Sample rows',
        type: 'number',
        help: 'Rows of each result shown to the model.',
      },
      {
        key: 'sampleChars',
        label: 'Sample characters',
        type: 'number',
        help: 'Cap on the characters of one sample.',
      },
      {
        key: 'maxQueryRows',
        label: 'Max query rows',
        type: 'number',
        help: 'Rows a literal query may return before it is truncated.',
      },
      {
        key: 'schemaMode',
        label: 'Schema mode',
        type: 'select',
        options: [
          { value: 'full', label: 'Full' },
          { value: 'targeted', label: 'Targeted' },
        ],
        help: 'Full: every entity file in the prompt. Targeted: only the routed ones.',
      },
    ],
  },
  {
    id: 'features',
    title: 'Behaviour',
    description: 'What the assistant may do in this scope.',
    keys: [
      {
        key: 'html',
        label: 'HTML answers',
        type: 'boolean',
        help: 'Let the model return rich HTML rows at all.',
      },
      {
        key: 'htmlMode',
        label: 'HTML use',
        type: 'select',
        options: [
          { value: 'sparing', label: 'Sparing (last resort)' },
          { value: 'free', label: 'Free' },
          { value: 'off', label: 'Off' },
        ],
        help: 'Sparing: one row of at most ~4k characters when no chart or table fits. Free: whenever the model likes (the most expensive output there is).',
      },
      {
        key: 'clarify',
        label: 'Clarifying questions',
        type: 'boolean',
        help: 'The model may ask back before running an ambiguous question.',
      },
      {
        key: 'queryLogs',
        label: 'Query logs',
        type: 'boolean',
        help: 'The model may read the workserver logs.',
      },
      {
        key: 'searchDocs',
        label: 'Documentation search',
        type: 'boolean',
        help: 'The model may search the indexed documentation.',
      },
      {
        key: 'docLinks',
        label: 'Documentation links',
        type: 'select',
        options: [
          { value: 'internal', label: 'Internal Confluence links' },
          { value: 'external', label: 'External Share links' },
        ],
        help: 'Which link a documentation citation carries.',
      },
      {
        key: 'enableAgenticMonitoring',
        label: 'Agentic monitoring',
        type: 'boolean',
        help: 'Kill switch of the monitor scheduler: running monitors finish, nothing new is claimed.',
        globalOnly: true,
        default: true,
      },
    ],
  },
  {
    id: 'context',
    title: 'Context',
    description: 'The cached prompt prefix: how much schema goes in and whether Vertex caches it explicitly.',
    keys: [
      {
        key: 'prefixMode',
        label: 'Prefix mode',
        type: 'select',
        options: [
          { value: 'auto', label: 'Automatic' },
          { value: 'routed', label: 'Routed files only' },
          { value: 'full', label: 'Full schema' },
        ],
        help: 'Routed: a route call picks the files per question. Full: every entity file in a byte-identical prefix the implicit cache shares across users — cheaper with steady traffic (see the cache hit rate under Usage).',
      },
      {
        key: 'explicitCache',
        label: 'Explicit cache',
        type: 'select',
        options: [
          { value: 'auto', label: 'Automatic (when large enough)' },
          { value: 'off', label: 'Off' },
          { value: 'on', label: 'On' },
        ],
        help: 'Vertex explicit context caching of the prefix.',
      },
    ],
  },
  {
    id: 'locale',
    title: 'Locale',
    description: 'Where the model runs and how dates and texts are presented.',
    keys: [
      {
        key: 'location',
        label: 'Vertex location',
        type: 'text',
        help: 'eu, global, us or a region such as europe-west1.',
        placeholder: 'eu',
      },
      { key: 'timezone', label: 'Time zone', type: 'text', help: 'IANA name, e.g. Europe/Stockholm.' },
      {
        key: 'defaultLanguage',
        label: 'Default language',
        type: 'text',
        help: 'For users without a preference, e.g. en or sv.',
        placeholder: 'en',
      },
    ],
  },
];

const formatValue = (spec: SettingSpec, v: unknown): string => {
  if (v == null || v === '') return '—';
  if (spec.type === 'boolean') return v ? 'on' : 'off';
  if (spec.type === 'select') return spec.options?.find((o) => o.value === String(v))?.label ?? String(v);
  return String(v);
};

const without = <T extends object>(o: T, key: keyof T): T => {
  const next = { ...o };
  delete next[key];
  return next;
};

/** A number typed as text: half-typed input ("", "-") stays on screen instead of becoming 0. */
function NumberInput({
  label,
  value,
  disabled,
  onValue,
}: {
  label: string;
  value: number | undefined;
  disabled: boolean;
  /** `undefined` = emptied. */
  onValue: (v: number | undefined) => void;
}) {
  const [text, setText] = useState(value == null ? '' : String(value));
  useEffect(() => {
    setText((cur) => (Number(cur) === value && cur.trim() !== '' ? cur : value == null ? '' : String(value)));
  }, [value]);
  return (
    <TextField
      aria-label={label}
      type="number"
      inputMode="numeric"
      value={text}
      disabled={disabled}
      onChange={(e) => {
        setText(e.target.value);
        const t = e.target.value.trim();
        if (t === '') onValue(undefined);
        else if (Number.isFinite(Number(t))) onValue(Number(t));
      }}
    />
  );
}

/** One row: label + override checkbox, control, help, inherited value + reset. */
function SettingRow({
  name,
  testKey,
  overridden,
  canOverride,
  disabled,
  help,
  inheritedText,
  onOverride,
  onReset,
  overrideLabel,
  children,
}: {
  name: string;
  testKey: string;
  overridden: boolean;
  canOverride: boolean;
  disabled?: boolean;
  help?: string;
  inheritedText: string | null;
  onOverride: (on: boolean) => void;
  onReset: () => void;
  overrideLabel: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`bd-setting${overridden ? ' bd-setting-overridden' : ''}`} data-setting={testKey}>
      <div className="bd-setting-label">
        <span className="bd-setting-name">{name}</span>
        {canOverride && (
          <label className="nyk-check nyk-check-sm">
            <input
              type="checkbox"
              aria-label={overrideLabel}
              checked={overridden}
              disabled={disabled}
              onChange={(e) => onOverride(e.target.checked)}
            />
            <span>Override</span>
          </label>
        )}
      </div>
      <div className="bd-setting-control">{children}</div>
      {help && <div className="nyk-ui-hint">{help}</div>}
      {inheritedText != null && (
        <div className="bd-setting-inherited nyk-ui-hint">
          <span>inherited: {inheritedText}</span>
          {overridden && !disabled && (
            <button type="button" className="nyk-link nyk-btn-link" onClick={onReset}>
              Reset to inherited
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Grouped engine settings. On an override level a field shows the inherited value (disabled) until "Override";
 * "Reset to inherited" drops the key again. `allRequired` = the global level: every key edited directly.
 */
export function ModelSettingsEditor({
  value,
  onChange,
  inherited = {},
  allRequired,
  disabled,
}: {
  value: ModelSettingsOverrides;
  onChange: (v: ModelSettingsOverrides) => void;
  inherited?: ModelSettingsOverrides;
  allRequired?: boolean;
  disabled?: boolean;
}) {
  const set = (key: keyof ModelSettings, v: unknown) => onChange({ ...value, [key]: v });
  const clear = (key: keyof ModelSettings) => onChange(without(value, key));
  const groups = useMemo(
    () =>
      SETTING_GROUPS.map((g) => ({ ...g, keys: g.keys.filter((k) => allRequired || !k.globalOnly) })).filter(
        (g) => g.keys.length
      ),
    [allRequired]
  );
  return (
    <div className="bd-settings" data-testid="settings-editor">
      {groups.map((g) => (
        <section key={g.id} className="bd-settings-group" data-settings-group={g.id}>
          <div className="bd-settings-head">
            <h3>{g.title}</h3>
            <p className="nyk-ui-hint">{g.description}</p>
          </div>
          <div className="bd-settings-fields">
            {g.keys.map((spec) => {
              const { key, type } = spec;
              const overridden = allRequired || key in value;
              const current = (overridden ? value[key] : inherited[key]) ?? spec.default;
              const editable = !disabled && overridden;
              const control =
                type === 'boolean' ? (
                  <Switch
                    checked={!!current}
                    onChange={(v) => set(key, v)}
                    disabled={!editable}
                    label={current ? 'On' : 'Off'}
                  />
                ) : type === 'select' ? (
                  <Select
                    aria-label={spec.label}
                    value={String(current ?? '')}
                    onChange={(v) => (v === '' ? clear(key) : set(key, v))}
                    options={[
                      ...(spec.optional || current == null || current === ''
                        ? [{ value: '', label: spec.optional ? 'Default' : 'Not set' }]
                        : []),
                      ...(spec.options ?? []),
                    ]}
                    disabled={!editable}
                  />
                ) : type === 'number' ? (
                  <NumberInput
                    label={spec.label}
                    value={current as number | undefined}
                    disabled={!editable}
                    onValue={(n) => (n === undefined ? allRequired && clear(key) : set(key, n))}
                  />
                ) : (
                  <TextField
                    aria-label={spec.label}
                    value={current == null ? '' : String(current)}
                    placeholder={spec.placeholder}
                    onChange={(e) => set(key, e.target.value)}
                    disabled={!editable}
                  />
                );
              return (
                <SettingRow
                  key={key}
                  name={spec.label}
                  testKey={key}
                  overridden={overridden}
                  canOverride={!allRequired}
                  disabled={disabled}
                  help={spec.help}
                  inheritedText={allRequired ? null : formatValue(spec, inherited[key] ?? spec.default)}
                  overrideLabel={`override ${key}`}
                  onOverride={(on) =>
                    on
                      ? set(
                          key,
                          inherited[key] ??
                            spec.default ??
                            (type === 'number' ? 0 : type === 'boolean' ? false : '')
                        )
                      : clear(key)
                  }
                  onReset={() => clear(key)}
                >
                  {control}
                </SettingRow>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

export interface FeatureFlagSpec {
  key: string;
  label: string;
  help?: string;
  /** Assumed when no level sets the flag. */
  default?: boolean;
}

/** The chat's and the API's feature flags in screen order; unknown stored keys are shown too. */
export const FEATURE_FLAGS: FeatureFlagSpec[] = [
  {
    key: 'history',
    label: 'Conversation history',
    help: 'The conversation list and reload of earlier turns.',
  },
  { key: 'savedResponses', label: 'Saved responses', help: 'Save an answer and run it again later.' },
  { key: 'usage', label: 'Usage panel', help: 'The token usage and limit dialog.' },
  { key: 'settings', label: 'User settings', help: 'Language, font size, e-mail address, raw data.' },
  { key: 'feedback', label: 'Feedback', help: 'Thumbs up/down with a comment on every answer.' },
  { key: 'pdfExport', label: 'PDF export', help: 'Download an answer as PDF.' },
  { key: 'thinkingPicker', label: 'Thinking picker', help: 'Normal / high / expert in the composer.' },
  {
    key: 'attachments',
    label: 'File attachments',
    help: 'The attachment button (the API gate is fileUpload).',
  },
  { key: 'fileUpload', label: 'File upload (API gate)', help: 'Accept uploaded files in the chat API.' },
  { key: 'memory', label: 'Memory notes', help: 'Remember facts across conversations.' },
  { key: 'htmlRows', label: 'HTML rows', help: 'Let the model answer with sanitised HTML rows.' },
  { key: 'sendMail', label: 'Send e-mail', help: 'E-mail actions in the chat (group NYCEBUDDY_SENDMAIL).' },
  { key: 'monitors', label: 'Monitors', help: 'Monitoring agents in the chat.' },
  {
    key: 'scheduledReports',
    label: 'Scheduled reports',
    help: 'Schedule an answer as a recurring e-mail card.',
    default: true,
  },
  {
    key: 'configToolDocs',
    label: 'Configuration tool documentation',
    help: 'search_docs also reaches the configuration tool corpus.',
    default: true,
  },
  {
    key: 'customerDocs',
    label: 'Customer documents',
    help: 'Documents uploaded for an organization or system, searched by search_docs for it only.',
  },
  {
    key: 'enableChatAgentCreation',
    label: 'Create agents from the chat',
    help: 'The createMonitorAgent tool for users in NYCEBUDDY_CREATEAGENT.',
  },
  { key: 'mcp', label: 'MCP keys', help: 'Personal MCP connection keys in the settings dialog.' },
  {
    key: 'topicGuard',
    label: 'Topic guard',
    help: 'Decline questions clearly unrelated to NYCE.LOGIC or logistics with a one-line reply.',
    default: true,
  },
];

/** Effective flag value: the stored overrides on top of the known defaults. */
export const flagOn = (flags: FeatureOverrides, key: string) =>
  flags[key] ?? FEATURE_FLAGS.find((f) => f.key === key)?.default ?? false;

/** Feature flags with the same inherit/override pattern as the settings editor. */
export function FeatureFlagsEditor({
  value,
  onChange,
  inherited = {},
  allRequired,
  disabled,
}: {
  value: FeatureOverrides;
  onChange: (v: FeatureOverrides) => void;
  inherited?: FeatureOverrides;
  allRequired?: boolean;
  disabled?: boolean;
}) {
  const set = (key: string, v: boolean) => onChange({ ...value, [key]: v });
  const clear = (key: string) => onChange(without(value, key));
  const specs = useMemo(() => {
    const known = new Set(FEATURE_FLAGS.map((f) => f.key));
    const extra = [...new Set([...Object.keys(value), ...Object.keys(inherited)])]
      .filter((k) => !known.has(k))
      .map((k): FeatureFlagSpec => ({
        key: k,
        label: k,
        help: 'Stored flag the console has no description for.',
      }));
    return [...FEATURE_FLAGS, ...extra];
  }, [value, inherited]);
  return (
    <div className="bd-settings" data-testid="feature-flags">
      <section className="bd-settings-group">
        <div className="bd-settings-head">
          <h3>Product features</h3>
          <p className="nyk-ui-hint">
            {allRequired
              ? 'What the chat offers unless an organization, tenant or system overrides it.'
              : 'Off hides the feature; an inherited flag follows the level above until overridden here.'}
          </p>
        </div>
        <div className="bd-settings-fields">
          {specs.map((spec) => {
            const { key } = spec;
            const overridden = allRequired || key in value;
            const fallback = inherited[key] ?? spec.default ?? false;
            const current = key in value ? !!value[key] : fallback;
            return (
              <SettingRow
                key={key}
                name={spec.label}
                testKey={`flag:${key}`}
                overridden={overridden}
                canOverride={!allRequired}
                disabled={disabled}
                help={spec.help}
                inheritedText={allRequired ? null : fallback ? 'on' : 'off'}
                overrideLabel={`override feature ${key}`}
                onOverride={(on) => (on ? set(key, fallback) : clear(key))}
                onReset={() => clear(key)}
              >
                <Switch
                  checked={current}
                  onChange={(v) => set(key, v)}
                  disabled={disabled || !overridden}
                  label={current ? 'On' : 'Off'}
                />
              </SettingRow>
            );
          })}
        </div>
      </section>
    </div>
  );
}
